import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { DesktopContext } from '@/components/Desktop'
import { useStorage } from '@/contexts/StorageContext'
import useIsMobile from '@/hooks/useIsMobile'
import ArtifactWindow from './ArtifactWindow'

/**
 * Manages all open artifact windows, handling focus, z-index, and window cycling.
 */
export default function ArtifactWindowManager() {
  const { openWindows, closeArtifact } = useStorage()
  const desktopContext = useContext(DesktopContext)
  const isMobile = useIsMobile()

  // Track z-index for each artifact window (independent from desktop windows)
  const [windowZIndexes, setWindowZIndexes] = useState<Map<number, number>>(
    new Map()
  )
  const prevWindowCountRef = useRef(0)
  const nextZIndexRef = useRef(1000) // Artifact windows start at z-index 1000

  const activeWindowId = desktopContext?.activeArtifactWindowId ?? null
  const setActiveWindowId =
    desktopContext?.setActiveArtifactWindowId ?? (() => {})

  // Initialize z-indexes for new windows with a high base value
  useEffect(() => {
    const currentIds = new Set(openWindows.map((w) => w.windowId))
    const newZIndexes = new Map(windowZIndexes)
    let hasChanges = false

    // Add base z-indexes for new windows
    openWindows.forEach((window) => {
      if (!newZIndexes.has(window.windowId)) {
        newZIndexes.set(window.windowId, nextZIndexRef.current++)
        hasChanges = true
      }
    })

    // Remove z-indexes for closed windows
    Array.from(newZIndexes.keys()).forEach((id) => {
      if (!currentIds.has(id)) {
        newZIndexes.delete(id)
        hasChanges = true
      }
    })

    if (hasChanges) {
      setWindowZIndexes(newZIndexes)
    }
  }, [openWindows, windowZIndexes])

  // Get z-index for a window - only returns existing values, never updates state
  const getZIndex = useCallback(
    (windowId: number) => {
      return windowZIndexes.get(windowId) ?? 1000
    },
    [windowZIndexes]
  )

  // Handle window focus - increment internal z-index counter
  const handleFocus = useCallback(
    (windowId: number) => {
      if (activeWindowId === windowId) return

      const newZIndex = nextZIndexRef.current++
      setWindowZIndexes((prev) => new Map(prev).set(windowId, newZIndex))
      setActiveWindowId(windowId)
    },
    [activeWindowId, setActiveWindowId]
  )

  // Handle window close
  const handleClose = useCallback(
    (windowId: number) => {
      closeArtifact(windowId)
      setWindowZIndexes((prev) => {
        const newMap = new Map(prev)
        newMap.delete(windowId)
        return newMap
      })
      if (activeWindowId === windowId) {
        // Focus another window if available
        const remainingWindows = openWindows.filter(
          (w) => w.windowId !== windowId
        )
        if (remainingWindows.length > 0) {
          const lastWindowId =
            remainingWindows[remainingWindows.length - 1].windowId
          handleFocus(lastWindowId)
        } else {
          setActiveWindowId(null)
        }
      }
    },
    [closeArtifact, activeWindowId, openWindows, setActiveWindowId, handleFocus]
  )

  // Auto-focus new windows when they're opened (but not when windows are blurred)
  useEffect(() => {
    const currentCount = openWindows.length
    const prevCount = prevWindowCountRef.current

    // Only auto-focus if a NEW window was added (count increased)
    if (currentCount > prevCount && currentCount > 0) {
      // Focus the newest window
      const newestWindow = openWindows[openWindows.length - 1]
      handleFocus(newestWindow.windowId)
    }

    prevWindowCountRef.current = currentCount
  }, [openWindows, handleFocus])

  return (
    <>
      {openWindows.map((window) => (
        <ArtifactWindow
          key={window.id}
          artifactId={window.artifactId}
          windowId={window.windowId}
          isActive={window.windowId === activeWindowId}
          onFocus={() => handleFocus(window.windowId)}
          onClose={() => handleClose(window.windowId)}
          zIndex={getZIndex(window.windowId)}
          isMobile={isMobile}
        />
      ))}
    </>
  )
}
